import { registerBuiltInLoaders } from "@babylonjs/loaders/dynamic";

import "./styles.css";

import { gameConfig } from "@/config/gameConfig";
import { Game } from "@/core/Game";
import { sceneRegistry } from "@/scenes";
import { describeError, logger } from "@/utils/logger";

declare global {
  interface Window {
    /**
     * Poignée de debug exposée uniquement en développement :
     * `__game.engine`, `__game.input`, `__game.currentSceneId` depuis la console.
     */
    __game?: Game;
  }
}

// Enregistre les fabriques de loaders (glTF, OBJ, STL…). Elles sont importées
// à la demande, uniquement si une scène charge réellement un fichier du format
// concerné : aucun coût sur le bundle initial.
registerBuiltInLoaders();

const overlay = document.querySelector<HTMLElement>("#overlay");
const overlayDetail = document.querySelector<HTMLElement>("#overlay-detail");
const hud = document.querySelector<HTMLElement>("#hud");
const hudBackend = document.querySelector<HTMLElement>("#hud-backend");
const hudFps = document.querySelector<HTMLElement>("#hud-fps");

function hideOverlay(): void {
  if (!overlay) return;
  overlay.classList.add("overlay--fading");
  // Attend la fin de la transition avant de retirer l'élément du flux.
  overlay.addEventListener(
    "transitionend",
    () => {
      overlay.hidden = true;
    },
    { once: true },
  );
}

function showFatalError(message: string): void {
  if (!overlay) return;
  overlay.hidden = false;
  overlay.classList.remove("overlay--fading");
  overlay.classList.add("overlay--error");

  const title = overlay.querySelector<HTMLElement>(".overlay__title");
  if (title) title.textContent = "Le jeu n'a pas pu démarrer";
  if (overlayDetail) overlayDetail.textContent = message;
}

async function bootstrap(): Promise<void> {
  const game = await Game.create(gameConfig, sceneRegistry);
  game.start();
  hideOverlay();

  if (hud && hudBackend) {
    hud.hidden = false;
    hudBackend.innerHTML = `Rendu <strong>${game.backend.toUpperCase()}</strong>`;
  }

  if (hudFps) {
    // L'observable de fin d'image est déjà cadencé par le moteur ; on n'affiche
    // qu'une fois sur dix pour garder un compteur lisible.
    let frame = 0;
    game.engine.onEndFrameObservable.add(() => {
      if (frame++ % 10 !== 0) return;
      // Sur la toute première image, le delta vaut 0 et getFps() rend Infinity.
      const fps = game.engine.getFps();
      hudFps.innerHTML = `<strong>${Number.isFinite(fps) ? Math.round(fps) : 0}</strong> fps`;
    });
  }

  if (import.meta.env.DEV) {
    // Poignée de debug : `__game` donne accès au moteur et aux scènes depuis la
    // console du navigateur. Absente du build de production.
    window.__game = game;
  }

  if (import.meta.env.DEV && gameConfig.enableInspector) {
    game.input.onActionPressed.add((action) => {
      if (action !== "toggleDebug") return;
      const scene = game.engine.scenes[0];
      if (!scene) return;

      void import("@/debug/inspector").then(({ toggleInspector }) => toggleInspector(scene));
    });
    logger.info("Inspecteur disponible : appuyer sur ` (touche sous Échap).");
  }

  // HMR : sans destruction explicite, chaque rechargement à chaud empile un
  // moteur WebGL de plus et finit par saturer les contextes du navigateur.
  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      game.dispose();
    });
  }
}

bootstrap().catch((error: unknown) => {
  const message = describeError(error);
  logger.error(`Démarrage impossible : ${message}`);
  showFatalError(message);
});
